import type { ComponentPropsWithoutRef, ReactNode, Ref, UIEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

const SCROLL_IDLE_MS = 650;

interface ScrollMetrics {
  isScrollable: boolean;
  progress: number;
  thumbHeightPct: number;
  thumbTopPct: number;
}

const DEFAULT_METRICS: ScrollMetrics = {
  isScrollable: false,
  progress: 0,
  thumbHeightPct: 100,
  thumbTopPct: 0,
};

export interface ScrollViewportProps
  extends Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'className'> {
  children: ReactNode;
  className?: string;
  viewportClassName?: string;
  contentClassName?: string;
  viewportRef?: Ref<HTMLDivElement>;
  showIndicator?: boolean;
}

export function ScrollViewport({
  children,
  className,
  viewportClassName,
  contentClassName,
  viewportRef,
  onScroll,
  showIndicator = false,
  ...viewportProps
}: ScrollViewportProps): JSX.Element {
  const internalViewportRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const hideTimerRef = useRef<number | undefined>(undefined);
  const syncMetricsRef = useRef<() => void>(() => {});

  const [metrics, setMetrics] = useState<ScrollMetrics>(DEFAULT_METRICS);
  const [isScrolling, setIsScrolling] = useState(false);
  const metricsRef = useRef(metrics);
  const isScrollingRef = useRef(isScrolling);

  syncMetricsRef.current = (): void => {
    const next = readScrollMetrics(internalViewportRef.current);
    if (!areMetricsEqual(metricsRef.current, next)) {
      metricsRef.current = next;
      setMetrics(next);
    }
    if (!next.isScrollable && isScrollingRef.current) {
      isScrollingRef.current = false;
      setIsScrolling(false);
    }
  };

  useEffect(() => {
    syncMetricsRef.current();
  });

  useEffect(() => {
    syncMetricsRef.current();

    const onResize = (): void => {
      syncMetricsRef.current();
    };

    window.addEventListener('resize', onResize);

    if (typeof ResizeObserver === 'undefined') {
      return () => {
        window.removeEventListener('resize', onResize);
      };
    }

    const observer = new ResizeObserver(() => {
      syncMetricsRef.current();
    });

    if (internalViewportRef.current) observer.observe(internalViewportRef.current);
    if (contentRef.current) observer.observe(contentRef.current);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(
    () => () => {
      if (hideTimerRef.current !== undefined) {
        window.clearTimeout(hideTimerRef.current);
      }
    },
    [],
  );

  const setViewportNode = (node: HTMLDivElement | null): void => {
    internalViewportRef.current = node;
    if (!viewportRef) return;
    if (typeof viewportRef === 'function') {
      viewportRef(node);
      return;
    }
    (viewportRef as { current: HTMLDivElement | null }).current = node;
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
    onScroll?.(event);
    syncMetricsRef.current();
    if (!isScrollingRef.current) {
      isScrollingRef.current = true;
      setIsScrolling(true);
    }
    if (hideTimerRef.current !== undefined) {
      window.clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = window.setTimeout(() => {
      isScrollingRef.current = false;
      setIsScrolling(false);
    }, SCROLL_IDLE_MS);
  };

  const indicatorVisible = showIndicator && metrics.isScrollable && isScrolling;

  return (
    <div className={joinClassNames('relative min-h-0', className)}>
      <div
        {...viewportProps}
        ref={setViewportNode}
        onScroll={handleScroll}
        className={joinClassNames(
          'scrollbar-hidden min-h-0 h-full overflow-y-auto',
          viewportClassName,
        )}
      >
        <div ref={contentRef} className={contentClassName}>
          {children}
        </div>
      </div>

      {showIndicator && metrics.isScrollable && (
        <div
          aria-hidden="true"
          data-scroll-indicator="true"
          data-visible={indicatorVisible || undefined}
          className={joinClassNames(
            'pointer-events-none absolute inset-y-2 right-0.5 flex justify-end transition-opacity duration-200',
            indicatorVisible ? 'opacity-100' : 'opacity-0',
          )}
        >
          <div className="relative h-full w-1 rounded-full bg-slate-200/85 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.22)]">
            <div
              className="absolute inset-x-0 rounded-full bg-slate-700/80 shadow-sm"
              style={{
                height: `${metrics.thumbHeightPct}%`,
                top: `${metrics.thumbTopPct}%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function readScrollMetrics(viewport: HTMLDivElement | null): ScrollMetrics {
  if (!viewport) return DEFAULT_METRICS;

  const clientHeight = viewport.clientHeight;
  const scrollHeight = viewport.scrollHeight;
  const maxScrollTop = scrollHeight - clientHeight;

  if (clientHeight <= 0 || maxScrollTop <= 1) return DEFAULT_METRICS;

  const progress = clamp(viewport.scrollTop / maxScrollTop, 0, 1);
  const thumbHeightPct = clamp((clientHeight / scrollHeight) * 100, 12, 100);
  const thumbTopPct = progress * (100 - thumbHeightPct);

  return {
    isScrollable: true,
    progress: round(progress),
    thumbHeightPct: round(thumbHeightPct),
    thumbTopPct: round(thumbTopPct),
  };
}

function areMetricsEqual(left: ScrollMetrics, right: ScrollMetrics): boolean {
  return (
    left.isScrollable === right.isScrollable &&
    left.progress === right.progress &&
    left.thumbHeightPct === right.thumbHeightPct &&
    left.thumbTopPct === right.thumbTopPct
  );
}

function joinClassNames(...values: Array<string | undefined>): string {
  return values.filter(Boolean).join(' ');
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
